import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { useAuth } from "@/hooks/useAuth";
import { socket } from "@/lib/socket";
import { Gavel, Clock, TrendingUp, ShoppingCart, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface Bid {
    bid_id: number;
    bidder_id: number;
    amount: string;
    status: string;
    bid_at: string;
}

interface Listing {
    listing_id: number;
    title: string | null;
    description: string | null;
    year: number | null;
    make: string | null;
    model: string | null;
    vin: string | null;
    mileage: string | null;
    condition: string | null;
    current_bid: string | null;
    buy_now_price: string | null;
    starting_bid: string | null;
    reserve_price: string | null;
    auction_mode: string;
    auction_end: string | null;
    status: string;
    _count: { bids: number };
    bids: Bid[];
}

function useCountdown(end: string | null) {
    const [label, setLabel] = useState("");
    useEffect(() => {
        if (!end) return;
        const tick = () => {
            const ms = new Date(end).getTime() - Date.now();
            if (ms <= 0) { setLabel("Ended"); return; }
            const h = Math.floor(ms / 3600000);
            const m = Math.floor((ms % 3600000) / 60000);
            const s = Math.floor((ms % 60000) / 1000);
            setLabel(h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h left` : `${h}h ${m}m ${s}s left`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [end]);
    return label;
}

export default function AuctionListingPage() {
    const { id }       = useParams();
    const navigate     = useNavigate();
    const { user }     = useAuth();
    const countdown    = useRef<ReturnType<typeof useCountdown>>("").current;

    const [listing,    setListing]    = useState<Listing | null>(null);
    const [loading,    setLoading]    = useState(true);
    const [bidAmount,  setBidAmount]  = useState("");
    const [offerNote,  setOfferNote]  = useState("");
    const [bidLoading, setBidLoading] = useState(false);
    const [bidError,   setBidError]   = useState<string | null>(null);
    const [bidSuccess, setBidSuccess] = useState(false);
    const timeLabel = useCountdown(listing?.auction_end ?? null);

    useEffect(() => {
        axios.get(`${API_URL}/auction/listings/${id}`, { withCredentials: true })
            .then((r) => setListing(r.data.listing ?? null))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (!id) return;
        socket.emit("joinAuction", Number(id));

        socket.on("auctionBid", ({ listingId, amount }: { listingId: number; amount: string }) => {
            if (listingId === Number(id)) {
                setListing((prev) => prev ? { ...prev, current_bid: amount, _count: { bids: prev._count.bids + 1 } } : prev);
            }
        });

        socket.on("auctionEnded", ({ listingId }: { listingId: number }) => {
            if (listingId === Number(id)) {
                setListing((prev) => prev ? { ...prev, status: "SOLD" } : prev);
            }
        });

        return () => {
            socket.emit("leaveAuction", Number(id));
            socket.off("auctionBid");
            socket.off("auctionEnded");
        };
    }, [id]);

    const handleBid = async () => {
        if (!user) { navigate("/customer/login"); return; }
        setBidLoading(true);
        setBidError(null);
        try {
            const res = await axios.post(
                `${API_URL}/auction/listings/${id}/bid`,
                { amount: bidAmount },
                { withCredentials: true }
            );
            if (res.data.ok) { setBidSuccess(true); setBidAmount(""); }
        } catch (e: any) {
            setBidError(e?.response?.data?.message ?? "Bid failed.");
        } finally {
            setBidLoading(false);
        }
    };

    const handleBuyNow = async () => {
        if (!user) { navigate("/customer/login"); return; }
        try {
            await axios.post(`${API_URL}/auction/listings/${id}/buy-now`, {}, { withCredentials: true });
            setListing((prev) => prev ? { ...prev, status: "SOLD" } : prev);
        } catch (e: any) {
            setBidError(e?.response?.data?.message ?? "Buy Now failed.");
        }
    };

    const handleOffer = async () => {
        if (!user) { navigate("/customer/login"); return; }
        setBidLoading(true);
        try {
            await axios.post(
                `${API_URL}/auction/listings/${id}/offer`,
                { amount: bidAmount, notes: offerNote },
                { withCredentials: true }
            );
            setBidSuccess(true);
            setBidAmount("");
        } catch (e: any) {
            setBidError(e?.response?.data?.message ?? "Offer failed.");
        } finally {
            setBidLoading(false);
        }
    };

    if (loading) return <p className="p-8 text-center">Loading…</p>;
    if (!listing) return <p className="p-8 text-center text-muted-foreground">Listing not found.</p>;

    const vehicle = [listing.year, listing.make, listing.model].filter(Boolean).join(" ") || listing.title || "Vehicle";
    const isSold  = listing.status === "SOLD" || listing.status === "ENDED";

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
            <div className="bg-card rounded-2xl border p-6 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold">{vehicle}</h1>
                        {listing.vin && <p className="text-xs text-muted-foreground mt-1">VIN: {listing.vin}</p>}
                        {listing.mileage && <p className="text-sm text-muted-foreground">{listing.mileage} miles · {listing.condition ?? "—"}</p>}
                    </div>
                    {listing.auction_end && !isSold && (
                        <div className="flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-orange-700 text-sm font-medium">
                            <Clock className="h-4 w-4" />
                            {timeLabel}
                        </div>
                    )}
                    {isSold && (
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">SOLD</span>
                    )}
                </div>

                {listing.description && (
                    <p className="text-sm text-muted-foreground border-t pt-4">{listing.description}</p>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
                {/* Price panel */}
                <div className="bg-card rounded-xl border p-5 space-y-4">
                    {listing.auction_mode === "TIMED" && (
                        <>
                            <div>
                                <p className="text-xs text-muted-foreground">Current Bid</p>
                                <p className="text-3xl font-bold">${listing.current_bid ?? listing.starting_bid ?? "—"}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                    <TrendingUp className="h-3 w-3" /> {listing._count.bids} total bids
                                </p>
                            </div>
                            {!isSold && (
                                <div className="space-y-2">
                                    <Input
                                        type="number"
                                        placeholder="Your bid amount ($)"
                                        value={bidAmount}
                                        onChange={(e) => setBidAmount(e.target.value)}
                                    />
                                    {bidError && <p className="text-xs text-red-500">{bidError}</p>}
                                    {bidSuccess && <p className="text-xs text-green-600">Bid placed!</p>}
                                    <Button className="w-full bg-indigo-600 text-white hover:bg-indigo-700" onClick={handleBid} disabled={bidLoading}>
                                        <Gavel className="h-4 w-4 mr-2" />
                                        {bidLoading ? "Placing…" : "Place Bid"}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {listing.auction_mode === "BUY_NOW" && (
                        <>
                            <div>
                                <p className="text-xs text-muted-foreground">Buy Now Price</p>
                                <p className="text-3xl font-bold text-green-700">${listing.buy_now_price ?? "—"}</p>
                            </div>
                            {!isSold && (
                                <>
                                    {bidError && <p className="text-xs text-red-500">{bidError}</p>}
                                    <Button className="w-full bg-green-600 text-white hover:bg-green-700" onClick={handleBuyNow}>
                                        <ShoppingCart className="h-4 w-4 mr-2" /> Buy Now
                                    </Button>
                                </>
                            )}
                        </>
                    )}

                    {listing.auction_mode === "OFFER" && (
                        <>
                            <div>
                                <p className="text-xs text-muted-foreground">Starting Price</p>
                                <p className="text-3xl font-bold">${listing.starting_bid ?? "—"}</p>
                            </div>
                            {!isSold && (
                                <div className="space-y-2">
                                    <Input type="number" placeholder="Your offer ($)" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} />
                                    <Input placeholder="Note to seller (optional)" value={offerNote} onChange={(e) => setOfferNote(e.target.value)} />
                                    {bidError && <p className="text-xs text-red-500">{bidError}</p>}
                                    {bidSuccess && <p className="text-xs text-green-600">Offer submitted!</p>}
                                    <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" onClick={handleOffer} disabled={bidLoading}>
                                        <MessageSquare className="h-4 w-4 mr-2" />
                                        {bidLoading ? "Submitting…" : "Make Offer"}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Bid history */}
                {listing.auction_mode === "TIMED" && (
                    <div className="bg-card rounded-xl border p-5 space-y-3">
                        <h2 className="font-semibold">Bid History</h2>
                        {listing.bids.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No bids yet. Be the first!</p>
                        ) : (
                            <ul className="space-y-2">
                                {listing.bids.map((bid) => (
                                    <li key={bid.bid_id} className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            Bidder #{bid.bidder_id} · {new Date(bid.bid_at).toLocaleTimeString()}
                                        </span>
                                        <span className="font-semibold">${bid.amount}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
